import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_SANDBOX,
  GET_SINGLE_PRODUCT_NEW,
} from '../../../Constants/Config';

const initialState = {
  loading: false,
  singleProductData: {},
  error: '',
  lastFetched: null,
  lastProductId: null,
  lastMerchantId: null,
  activeRequestId: null,
};

// Generate pening , fulfilled and rejected action type
export const fetchSingleProductData = createAsyncThunk(
  'singleProduct/fetchSingleProductData',
  async (data, { rejectWithValue, getState }) => {
    const state = getState();
    const { singleProduct } = state;
    const forceRefresh = Boolean(data?.forceRefresh);
    
    // Check if we have cached data for the same product and merchant
    const now = Date.now();
    const cacheValidDuration = 5 * 60 * 1000; // 5 minutes
    
    if (
      !forceRefresh &&
      singleProduct.lastFetched &&
      singleProduct.lastProductId === data.product_id &&
      singleProduct.lastMerchantId === data.merchant_id &&
      (now - singleProduct.lastFetched) < cacheValidDuration &&
      Object.keys(singleProduct.singleProductData).length > 0
    ) {
      return singleProduct.singleProductData;
    }
    try {
      // Create FormData object for multipart/form-data
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (key === 'forceRefresh') return;
        formData.append(key, data[key]);
      });

      const response = await axios.post(
        BASE_URL_SANDBOX + GET_SINGLE_PRODUCT_NEW,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      
      if (response.data.status === 200) {
        return response.data.result;
      } else {
        // Handle API errors (like status 400)
        return rejectWithValue({
          status: response.data.status,
          message: response.data.message || 'API request failed'
        });
      }
    } catch (error) {
      // Handle network errors
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message || 'Network error occurred'
      });
    }
  },
);

const SingleProductSlice = createSlice({
  name: 'singleProduct',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchSingleProductData.pending, (state, action) => {
      const nextProductId = action.meta?.arg?.product_id ?? null;
      const nextMerchantId = action.meta?.arg?.merchant_id ?? null;
      const requestContextChanged =
        String(state.lastProductId ?? '') !== String(nextProductId ?? '') ||
        String(state.lastMerchantId ?? '').toUpperCase() !==
          String(nextMerchantId ?? '').toUpperCase();

      state.loading = true;
      state.activeRequestId = action.meta.requestId;
      if (requestContextChanged) {
        state.singleProductData = {};
      }
      // Clear any stale error from a previous product so it can't leak forward
      // and bounce the next (valid) product before its fetch resolves.
      state.error = '';
      // Record the in-flight product so the component can scope terminal-state
      // handling to the product currently being requested.
      if (action.meta?.arg) {
        state.lastProductId = action.meta.arg.product_id;
        state.lastMerchantId = action.meta.arg.merchant_id;
      }
    });
    builder.addCase(fetchSingleProductData.fulfilled, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return;

      state.loading = false;
      state.activeRequestId = null;
      state.singleProductData = action.payload;
      state.error = '';
      state.lastFetched = Date.now();
      // Extract product_id and merchant_id from the action meta
      if (action.meta?.arg) {
        state.lastProductId = action.meta.arg.product_id;
        state.lastMerchantId = action.meta.arg.merchant_id;
      }
    });
    builder.addCase(fetchSingleProductData.rejected, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return;

      state.loading = false;
      state.activeRequestId = null;
      // Record which product this error belongs to so the component can scope
      // the error to the product currently in the URL.
      if (action.meta?.arg) {
        state.lastProductId = action.meta.arg.product_id;
        state.lastMerchantId = action.meta.arg.merchant_id;
      }
      // Only clear data if it's a real error, preserve existing data on network failures
      if (action.payload?.status === 400 || action.payload?.status === 404) {
        state.singleProductData = {};
      }
      // Handle both rejectWithValue and regular errors
      state.error = action.payload?.message || action.error?.message || 'An error occurred';
    });
  },
});

export default SingleProductSlice.reducer;
